import fetch from 'cross-fetch';
import * as qs from 'query-string';
import apiHelper, {
  SupportedIncomingFormat,
  SupportedMethod,
  SupportedServers,
} from './ApiHelper';
import { appConfig } from './ConfigLoader';
import { useAppSelector } from '../store/hooks';
import { setAppInterfaceData } from '../store/reducer/appInterfaceDataSlice';
import {
  AppStateAuthentication,
  selectAppUserProfile,
  setAppStateData,
  updateAppAuthentication,
  updateAppUserProfile,
  updateAppUserSubscription,
} from '../store/reducer/appStateSlice';
import { outsideComponentDispatch } from '../store/store';
import { AccountApiResponse } from '../view';

// Common Tools
// Modified from previous repo
/* eslint-disable */

// Data validator function
export interface VerifyDataFormatConfig {
  type:
    | 'CUSTOM'
    | 'ANYTEXT'
    | 'PASSWORD'
    | 'EMAIL'
    | 'NAME'
    | 'PHONENUMBER'
    | 'PHONENUMBERCOUNTRYCODE'
    | 'PHONENUMBERNUMBER'
    | 'GENDER'
    | 'DATE'
    | 'DATETIME'
    | 'BIRTHDATE'
    | 'TIME'
    | 'BIRTHDAY'
    | 'INTEGER'
    | 'POSITIVEINTEGER'
    | 'NEGATIVEINTEGER'
    | 'NEGATIVEINTEGER'
    | 'POSITIVENUMBER'
    | 'NEGATIVENUMBER';
  customRegex?: RegExp;
}

export const verifyDataFormat = (
  data: string,
  config: VerifyDataFormatConfig,
  setErrorMessage?: (error: string) => void,
): boolean => {
  // Configuration Explaination
  /*
      allowUndefined: boolean ,
      type: oneOf["PASSWORD", "EMAIL", "NAME", "PHONENUMBER",
                  "GENDER", "DATE", "TIME", "BIRTHDAY"
                  "DATETIME", "INTEGER", "POSTIVEINTEGER",
                  "NEGATIVEINTEGER", "POSITIVENUMBER", "NEGATIVENUMBER", "CUSTOM"]
      customRegex: regex expression,
  */

  if (config.type === 'CUSTOM') {
    // Proceed custom data format verification
    if (config.customRegex === undefined) {
      return false;
    }
    return config.customRegex.test(data);
  } else {
    // Proceed pre-defined data format verification
    switch (config.type) {
      case 'ANYTEXT':
        let text_re = /.{1,}/;
        return text_re.test(data);
      case 'PASSWORD':
        let password_re = /^[\d\w\.\$\@\#\!\~\*]{8,}$/; // Length and all content
        let password_re_ = /\d/; // Numbers
        let password_re__ = /[a-z]/; // Lowercase character
        let password_re___ = /[A-Z]/; // Uppcase
        // let password_re____ = /[\.\$\@\#\!\~\*]/; ///[\.\$\@\#\!\~\*]/;            // Special characters

        const lengthCheck = password_re.test(data);
        const numberCheck = password_re_.test(data);
        const lowerCheck = password_re__.test(data);
        const upperCheck = password_re___.test(data);
        // const specialCheck = password_re____.test(data);

        let error = '';

        if (!lengthCheck) error += 'Password is less than 8 characters long';
        if (!(numberCheck && lowerCheck && upperCheck)) {
          const missing = [];
          if (!lowerCheck) missing.push('a lowercase character');
          if (!upperCheck) missing.push('an uppercase character');
          if (!numberCheck) missing.push('a number');

          if (error === '') {
            error += 'Password is missing ';
          } else {
            error += ', and is missing ';
          }
          if (missing.length === 1) error += `${missing[0]}.`;
          else {
            error += `${missing.slice(0, -1).join(', ')} and ${
              missing[missing.length - 1]
            }`;
          }
        }

        if (error !== '') setErrorMessage?.(`${error}.`);

        return lengthCheck && numberCheck && lowerCheck && upperCheck;

      case 'EMAIL':
        let email_re =
          /^[\da-zA-Z\_\.+]{1,}\@[\da-zA-Z]+\.[\da-zA-Z]+(|\.[\da-zA-Z]+){0,}$/;
        return email_re.test(data);

      case 'NAME':
        let name_re = /^[a-zA-Z]{1,}(|(\s[a-zA-Z]{1,}){1,})$/;
        return name_re.test(data);

      case 'PHONENUMBER':
        let phone_re =
          /^\+(((852|60|65)\d{8}$)|((886)\d{9}$)|((86)\d{13}$)|((63|44|84|1)\d{10}$))/;
        // let phone_re = /^\+(((852|60|65)\d{8}$)|((886)\d{9}$)|((63|44|84|1)\d{10}$))/;
        return phone_re.test(data);
      case 'PHONENUMBERCOUNTRYCODE':
        let phone_country_re = /\+(852|886|86|60|63|65|44|84|1)/;
        return phone_country_re.test(data);
      case 'PHONENUMBERNUMBER':
        let phone_number_re = /^\d{8}$/;
        return phone_number_re.test(data);

      case 'GENDER':
        let gender_re = /^(MALE|FEMALE)$/;
        return gender_re.test(data);

      case 'DATE':
      case 'DATETIME':
        let date_re =
          /^(19|20)\d\d-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])T([01]\d|2[0-3])\:[0-5]\d\:[0-5]\d\.\d{3}Z$/;
        return date_re.test(data);

      case 'BIRTHDATE':
        let birth_date_re =
          /^(19|20)\d\d-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])T([01]\d|2[0-3])\:[0-5]\d\:[0-5]\d\.\d{3}Z$/;
        if (!birth_date_re.test(data)) return false;
        const current_date_iso = new Date().toISOString();
        return current_date_iso > data;

      case 'TIME':
        let time_re = /^([01]\d|2[0-3])\:[0-5]\d\:[0-5]\d\.\d{3}$/;
        return time_re.test(data);

      case 'BIRTHDAY':
        let birthday_re =
          /^(19|20|21)\d\d-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;
        return birthday_re.test(data);

      case 'INTEGER':
        return Number.isInteger(data);
      case 'POSITIVEINTEGER':
        return Number.isInteger(data) && parseInt(data) > 0;
      case 'NEGATIVEINTEGER':
        return Number.isInteger(data) && parseInt(data) < 0;
      case 'POSITIVENUMBER':
        return Number.isFinite(data) && parseFloat(data) > 0;
      case 'NEGATIVENUMBER':
        return Number.isFinite(data) && parseFloat(data) < 0;
      default:
        console.warn('UNBOUNDED DATA VALIDATION CASE');
        return false;
    }
  }
};

export const resumeUserSession = (): void => {
  // Get session info from session storage
  const bigbangSessionInfo = window.localStorage.getItem(
    'bigbang_session_info',
  );
  const bigbangSessionTarget = window.localStorage.getItem(
    'bigbang_session_target',
  );
  const previousSessionInfo =
    bigbangSessionInfo === null
      ? null
      : (JSON.parse(bigbangSessionInfo) as AppStateAuthentication);
  const isLandscape = !(window.innerWidth < window.innerHeight);
  const correctTarget = isLandscape ? 'BBL' : 'PP';

  // Check if the session is the same as returned
  if (
    previousSessionInfo?.needResetPassword !== undefined &&
    bigbangSessionTarget === correctTarget
  ) {
    console.debug('Restoring previous session');
    outsideComponentDispatch(updateAppAuthentication(previousSessionInfo));
  }
};

export const queryString = () => {
  return qs.parse(window.location.search) as Record<string, string>;
};

export const parseParamAndFetchSupscription = () => {
  // RESUME SESSION INFO
  // GET QueryString
  const parmsValue = queryString();

  // Get session info from session storage
  const bigbangSessionInfo = window.localStorage.getItem(
    'bigbang_session_info',
  );
  const previousSessionInfo = JSON.parse(
    bigbangSessionInfo ? bigbangSessionInfo : '{}',
  ) as AppStateAuthentication;

  if (!previousSessionInfo.token && !parmsValue.clientSessionId) {
    // UNAUTHORIZED, MOVE TO LOGIN PAGE
    window.location.href = '/Login';
  }

  // Check if the session is the same as returned
  if (
    previousSessionInfo !== undefined &&
    previousSessionInfo !== null &&
    (parmsValue.clientSessionId === undefined ||
      parmsValue.clientSessionId === previousSessionInfo.sessionId)
  ) {
    console.debug('Session information matched');
    outsideComponentDispatch(updateAppAuthentication(previousSessionInfo));
  } else {
    console.error(
      'Session information not matched!!!!!!',
      previousSessionInfo.sessionId,
      parmsValue.clientSessionId,
    );
    if (previousSessionInfo.token) {
      outsideComponentDispatch(updateAppAuthentication(previousSessionInfo));
    }

    // NOT MATCH, MOVE TO LOGIN PAGE
    window.location.href = '/Login';
  }
  // RESUME SESSION INFO ENDS

  // RELOAD USER PROFILE INFO
  const urlProfileInfo = `${appConfig().accountServer}user/UserProfile`;
  const jsonDataProfileInfo = {};
  const headerDataProfileInfo = {
    authorization: previousSessionInfo ? previousSessionInfo.token : '',
  };
  fetch(urlProfileInfo, {
    method: 'POST',
    headers: {
      ...headerDataProfileInfo,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(jsonDataProfileInfo),
  })
    .then(async (res): Promise<void> => {
      const result = (await res.json()) as AccountApiResponse;
      if (result.success) {
        outsideComponentDispatch(updateAppUserProfile(result.data));
        // SET FIRST CHILD AS DEFAULT IN THE BEGINNING
        outsideComponentDispatch(
          setAppInterfaceData({
            dataKey: 'childProfile',
            data: result.data?.childProfile.first,
          }),
        );
      }
    })
    .catch((err) => {
      console.debug('API ERROR', urlProfileInfo, err);
    });
  // RELOAD USER PROFILE INFO ENDS

  // RELOAD USER SUBSCRIPTION
  const urlUserSubscription = `${
    appConfig().accountServer
  }user/UserSubscriptionList`;
  const headerDataUserSubscription = {
    authorization: previousSessionInfo?.token ?? '',
  };
  const jsonDataUserSubscription = {
    validityOption: 'VALID_ONLY',
  };

  fetch(urlUserSubscription, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headerDataUserSubscription,
    },
    body: JSON.stringify(jsonDataUserSubscription),
  })
    .then(async (res): Promise<void> => {
      const result = (await res.json()) as AccountApiResponse;
      if (result) {
        // Login Successful
        outsideComponentDispatch(
          updateAppUserSubscription(result.data as any[]),
        );
      }
    })
    .catch((err) => {
      console.debug('API ERROR', urlUserSubscription, err);
    });
  // RELOAD USER SUBSCRIPTION ENDS
};

export const loadCourse = (): void => {
  // FETCH COURSE INFO
  const courseEndpoint = `content/courses`;
  apiHelper(
    SupportedServers.BBL,
    courseEndpoint,
    SupportedMethod.GET,
    {},
    {
      incomingFormat: SupportedIncomingFormat.JSON,
    },
  )
    .then((courses) => {
      console.info('COURSES: ', courses);
      if (courses.success) {
        outsideComponentDispatch(
          setAppInterfaceData({
            dataKey: 'course',
            data: courses.data,
          }),
        );
      }
    })
    .catch((err) => {
      console.debug('loadCourse ERROR', err);
    });
};

//
export const loadFullCurriculum = async () => {
  // FETCH CURRICULUM INFO
  try {
    const curriculumEndpoint = `content/fullCurriculum`;
    const resp = await apiHelper(
      SupportedServers.BBL,
      curriculumEndpoint,
      SupportedMethod.GET,
      {},
      {
        incomingFormat: SupportedIncomingFormat.JSON,
      },
    );

    //
    if (resp.success) {
      await structureFullCirriculumByLessonId(resp.data);

      outsideComponentDispatch(
        setAppInterfaceData({
          dataKey: 'fullCurriculum',
          data: resp.data,
        }),
      );
    }

    return resp.data;
  } catch (err: unknown) {
    console.debug('loadCurriculum ERROR', err);
  }
};

//
const structureFullCirriculumByLessonId = async (data: any) => {
  const result: any = {};

  //
  for (const row of data) {
    //
    for (const lesson of row.lessons) {
      if (result[lesson.id] === undefined) {
        result[lesson.id] = lesson;
      }
    }
  }
  //
  outsideComponentDispatch(
    setAppInterfaceData({
      dataKey: 'fullCurriculumByLesson',
      data: result,
    }),
  );

  return true;
};
// const calculateMaxNutsDetails = async (data: any) => {
//   //
//   const totalNuts: any = {
//     total: 0,
//     grade: {}
//   };

//   //
//   for(const row of data) {

//     if (totalNuts.grade[row.grade] === undefined) {
//       totalNuts.grade[row.grade] = {
//         gradeTotal: 0
//       };
//     }

//     if (totalNuts.grade[row.grade].topic === undefined) {
//       totalNuts.grade[row.grade].topic = {};
//     }

//     if (totalNuts.grade[row.grade].topic[row.id] === undefined) {
//       totalNuts.grade[row.grade].topic[row.id] = {
//         topicTotal: 0,
//         lesson: {}
//       };
//     }

//     let topicTotal = 0;
//     for(const topic of row.topics) {
//       if (totalNuts.grade[row.grade].topic[row.id].lesson[topic.id] === undefined) {
//         totalNuts.grade[row.grade].topic[row.id].lesson[topic.id] = {
//           video: {},
//         }
//       }

//       let lessonTotal = 0;
//       for (const lesson of topic.lessons) {
//         if (totalNuts.grade[row.grade].topic[row.id].lesson[topic.id].video[lesson.id] === undefined) {
//           totalNuts.grade[row.grade].topic[row.id].lesson[topic.id].video[lesson.id] = Number(lesson.lessonDetail.lessonNuts);
//           lessonTotal += Number(lesson.lessonDetail.lessonNuts);
//         }
//       }
//       totalNuts.grade[row.grade].topic[row.id].lesson[topic.id].lessonTotal = lessonTotal;
//       topicTotal += Number(lessonTotal);
//     }
//     totalNuts.grade[row.grade].topic[row.id].topicTotal = Number(topicTotal);

//     //
//     totalNuts.grade[row.grade].gradeTotal = Number(totalNuts.grade[row.grade].gradeTotal) + Number(topicTotal);
//     totalNuts.total = Number(totalNuts.total) + Number(topicTotal);
//   }

// }

//
export const logoutBbl = (): void => {
  window.localStorage.removeItem('bigbang_session_info');
  window.localStorage.removeItem('bigbang_session_target');

  //
  window.localStorage.removeItem('persist:root');

  window.location.href = '/Login';
};

export const redirectToLogin = (): void => {
  window.location.href = '/Login';
};

export const disableBrowserBackFunction = (global: Window) => {
  if (typeof global === 'undefined') {
    throw new Error('window is undefined');
  }

  const _hash = '!';
  const noBackPlease = () => {
    global.location.href += '#';

    // Making sure we have the fruit available for juice....
    // 50 milliseconds for just once do not cost much (^__^)
    global.setTimeout(() => {
      global.location.href += '!';
    }, 50);
  };

  // Earlier we had setInterval here....
  global.onhashchange = () => {
    if (global.location.hash !== _hash) {
      global.location.hash = _hash;
    }
  };

  global.onload = function () {
    noBackPlease();
  };
};
